/**
 * @dfp/policy-engine — lock cascade DSL.
 *
 * Given a CascadePolicy + how many HOURS a loan is overdue, evaluateCascade
 * returns the (stage, desiredLockState, flags) the device should be driven to.
 *
 * Hours — not days — is the threshold unit, so a daily loan can escalate within
 * hours, or immediately: a step at `fromOverdueHours: 0` with a locked desired
 * state hard-locks the instant the loan goes overdue, while a `0`-hour UNLOCKED
 * step followed by a later locked step expresses a grace period. There is no
 * separate "grace" concept — it is just the offset of the first lock step.
 *
 * Policies are chosen per repayment frequency from a CascadePolicySet, so
 * daily / weekly / monthly loans each follow their own schedule.
 *
 * Pure: no I/O, no clock. The caller derives `overdueHours` from the loan's
 * pay-by datetime + now (hoursBetween) and passes it in.
 *
 *   evaluateCascade(policy, 0)       -> first step (immediate)
 *   evaluateCascade(policy, 6)       -> step covering 6h overdue
 *   evaluateCascade(policy, 24 * 14) -> step covering 14 days overdue
 */

/** Lock cascade stage. Mirrors `@dfp/domain`'s `LockStage` numeric grade. */
export type CascadeStage = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7;

/**
 * Target lock state the policy wants. Mirrors `@dfp/provider-contract`'s
 * `DesiredLockState` — no UNKNOWN (you don't "want" UNKNOWN).
 */
export type CascadeDesiredState = "UNLOCKED" | "SOFT_LOCKED" | "HARD_LOCKED";

/** Repayment cadence a loan is billed on — selects which policy in a set applies. */
export type RepaymentFrequency = "daily" | "weekly" | "monthly";

/**
 * One row in a cascade table. From `fromOverdueHours` hours overdue and beyond
 * (until the next step takes over), the device should be at this stage + desired
 * state. The first step in a policy MUST be `fromOverdueHours: 0` so every input
 * is covered; set its desired state to UNLOCKED for a grace period, or to a
 * locked state for an immediate lock. Fractions are allowed (0.5 = 30 minutes).
 */
export interface CascadeStep {
  readonly fromOverdueHours: number;
  readonly stage: CascadeStage;
  readonly desired: CascadeDesiredState;
  /**
   * Optional bag for handlers downstream of the evaluator. The cascade DSL
   * itself does not interpret these — they are pass-through hints for e.g.
   * operator alerts, repossession flagging, dispute auto-open.
   */
  readonly flags?: ReadonlySet<CascadeFlag>;
}

/** Side-channel hints the cascade step carries. */
export type CascadeFlag =
  | "alert_operator"
  | "open_dispute"
  | "flag_for_repossession"
  | "notify_customer";

export interface CascadePolicy {
  /** Version string — embedded in the audit row when this policy fires. */
  readonly version: string;
  /** Steps sorted strictly ascending by `fromOverdueHours`. validateCascadePolicy enforces this. */
  readonly steps: readonly CascadeStep[];
}

/**
 * A full set of cascade policies — one per repayment frequency, plus a fallback
 * for loans with no recorded frequency (legacy hand-built schedules). This is
 * what a country config persists (`country_configs.config.cascade`); the runtime
 * picks the applicable policy with {@link policyFor}.
 */
export interface CascadePolicySet {
  /** Version string for the whole set — bumped when any member policy changes. */
  readonly version: string;
  readonly byFrequency: Readonly<Record<RepaymentFrequency, CascadePolicy>>;
  readonly fallback: CascadePolicy;
}

export interface CascadeDecision {
  readonly policyVersion: string;
  readonly stage: CascadeStage;
  readonly desired: CascadeDesiredState;
  readonly flags: ReadonlySet<CascadeFlag>;
  /** Hours-overdue range this step covers (inclusive lower, exclusive upper). */
  readonly fromOverdueHours: number;
  readonly untilOverdueHours: number | null;
}

export class InvalidCascadePolicyError extends Error {
  constructor(message: string) {
    super(`invalid cascade policy: ${message}`);
    this.name = "InvalidCascadePolicyError";
  }
}

/**
 * Run validation checks once at load time so the runtime evaluator can assume
 * well-formed input. Invariants: at least one step, the first step is at hour 0
 * (so every overdue input is covered), and steps strictly ascend by
 * `fromOverdueHours` (no duplicate or out-of-order thresholds).
 */
export function validateCascadePolicy(policy: CascadePolicy): void {
  if (policy.steps.length === 0) {
    throw new InvalidCascadePolicyError("policy must have at least one step");
  }
  if (policy.steps[0]?.fromOverdueHours !== 0) {
    throw new InvalidCascadePolicyError(
      `first step must have fromOverdueHours: 0 (got ${policy.steps[0]?.fromOverdueHours})`,
    );
  }
  for (let i = 1; i < policy.steps.length; i += 1) {
    const prev = policy.steps[i - 1]!;
    const curr = policy.steps[i]!;
    if (!(curr.fromOverdueHours > prev.fromOverdueHours)) {
      throw new InvalidCascadePolicyError(
        `steps must be strictly ascending by fromOverdueHours ` +
          `(step[${i - 1}]=${prev.fromOverdueHours}, step[${i}]=${curr.fromOverdueHours})`,
      );
    }
  }
}

/** Validate every policy in a set — each frequency plus the fallback. */
export function validateCascadePolicySet(set: CascadePolicySet): void {
  validateCascadePolicy(set.fallback);
  validateCascadePolicy(set.byFrequency.daily);
  validateCascadePolicy(set.byFrequency.weekly);
  validateCascadePolicy(set.byFrequency.monthly);
}

/**
 * Pick the policy for a loan's repayment frequency. Unknown / null frequencies
 * (legacy hand-built schedules) fall back to the set's `fallback` policy.
 */
export function policyFor(
  set: CascadePolicySet,
  frequency: string | null | undefined,
): CascadePolicy {
  if (frequency === "daily" || frequency === "weekly" || frequency === "monthly") {
    return set.byFrequency[frequency];
  }
  return set.fallback;
}

/**
 * Pick the step that covers `overdueHours`. Negative / non-finite inputs are
 * clamped to the first step (a loan can't be "minus three hours overdue"; treat
 * as the grace/clean step). Inputs past the last step keep the last step's
 * decision. Fractional hours are honoured — NOT floored — because the
 * reconciliation scan runs per-minute, so sub-hour steps are actionable.
 */
export function evaluateCascade(policy: CascadePolicy, overdueHours: number): CascadeDecision {
  validateCascadePolicy(policy);
  const clamped = Number.isFinite(overdueHours) && overdueHours > 0 ? overdueHours : 0;
  // Find the LAST step whose fromOverdueHours <= clamped.
  let chosenIndex = 0;
  for (let i = 0; i < policy.steps.length; i += 1) {
    if (policy.steps[i]!.fromOverdueHours <= clamped) {
      chosenIndex = i;
    } else {
      break;
    }
  }
  const chosen = policy.steps[chosenIndex]!;
  const next = policy.steps[chosenIndex + 1];
  return {
    policyVersion: policy.version,
    stage: chosen.stage,
    desired: chosen.desired,
    flags: chosen.flags ?? new Set<CascadeFlag>(),
    fromOverdueHours: chosen.fromOverdueHours,
    untilOverdueHours: next ? next.fromOverdueHours : null,
  };
}

/**
 * The `fromOverdueHours` of a policy's first LOCK step — the earliest step whose
 * desired state is not UNLOCKED. Returns null when the policy never locks (every
 * step UNLOCKED). This is the moment the cascade first drives a device lock for
 * that frequency; the loan-page "lock deadline" reads it. Steps are validated
 * ascending, so the first non-UNLOCKED hit is the earliest.
 */
export function firstLockOffsetHours(policy: CascadePolicy): number | null {
  for (const step of policy.steps) {
    if (step.desired !== "UNLOCKED") return step.fromOverdueHours;
  }
  return null;
}

/**
 * The `fromOverdueHours` of a policy's first HARD_LOCKED step — when the cascade
 * escalates to a full lock. Returns null when the policy never hard-locks for
 * that frequency. The loan page reads it to show, while a device is SOFT_LOCKED,
 * when the hard lock is coming. Steps are validated ascending, so the first
 * HARD_LOCKED hit is the earliest.
 */
export function firstHardLockOffsetHours(policy: CascadePolicy): number | null {
  for (const step of policy.steps) {
    if (step.desired === "HARD_LOCKED") return step.fromOverdueHours;
  }
  return null;
}

const HOURS_PER_DAY = 24;

/**
 * The TZ default cascade, in hours. Identical schedule to the original
 * day-based MVP table (docs/plan §P1.E) — 0/3/7/14/30 days = 0/72/168/336/720
 * hours — so the move to hour granularity is exactly behaviour-preserving
 * (`floor(h / 24) >= n` ⟺ `h >= 24n`):
 *
 * - 0-71h    (day 0-2):   clean.
 * - 72-167h  (day 3-6):   soft warning (lock-screen notice, device usable).
 * - 168-335h (day 7-13):  soft lock + customer SMS.
 * - 336-719h (day 14-29): hard lock + operator alert.
 * - 720h+    (day 30+):   hard lock + flag for repossession + open dispute.
 */
export const TZ_DEFAULT_CASCADE: CascadePolicy = {
  version: "tz-cascade-v1",
  steps: [
    { fromOverdueHours: 0, stage: 0, desired: "UNLOCKED" },
    {
      fromOverdueHours: 3 * HOURS_PER_DAY,
      stage: 1,
      desired: "SOFT_LOCKED",
      flags: new Set<CascadeFlag>(["notify_customer"]),
    },
    {
      fromOverdueHours: 7 * HOURS_PER_DAY,
      stage: 2,
      desired: "SOFT_LOCKED",
      flags: new Set<CascadeFlag>(["notify_customer"]),
    },
    {
      fromOverdueHours: 14 * HOURS_PER_DAY,
      stage: 3,
      desired: "HARD_LOCKED",
      flags: new Set<CascadeFlag>(["alert_operator", "notify_customer"]),
    },
    {
      fromOverdueHours: 30 * HOURS_PER_DAY,
      stage: 5,
      desired: "HARD_LOCKED",
      flags: new Set<CascadeFlag>(["alert_operator", "flag_for_repossession", "open_dispute"]),
    },
  ],
};

/**
 * The TZ default policy SET. For backward compatibility every frequency (and the
 * fallback) starts from the same TZ_DEFAULT_CASCADE, so behaviour is unchanged
 * until an operator customizes a frequency. Per-frequency tuning — e.g. an
 * immediate hard lock for daily loans, or a few-hours grace for weekly — is then
 * a config edit, not a code change.
 */
export const TZ_DEFAULT_CASCADE_SET: CascadePolicySet = {
  version: "tz-cascade-set-v1",
  byFrequency: {
    daily: TZ_DEFAULT_CASCADE,
    weekly: TZ_DEFAULT_CASCADE,
    monthly: TZ_DEFAULT_CASCADE,
  },
  fallback: TZ_DEFAULT_CASCADE,
};

/** Whole days between two epochs, floored. Retained for human-facing "Nd overdue" copy. */
export function daysBetween(from: Date, to: Date): number {
  const MS_PER_DAY = 86_400_000;
  return Math.floor((to.valueOf() - from.valueOf()) / MS_PER_DAY);
}

/** Fractional hours between two epochs — the cascade's overdue unit. */
export function hoursBetween(from: Date, to: Date): number {
  const MS_PER_HOUR = 3_600_000;
  return (to.valueOf() - from.valueOf()) / MS_PER_HOUR;
}

/* ──────────────────────────────────────────────────────────────────────────
 *  JSON (de)serialization boundary.
 *
 *  CascadeStep.flags is a Set (for O(1) `.has`), which JSON.stringify silently
 *  drops. Persisted config — the operator-editable `lock.cascade_policy_set`
 *  setting — therefore uses array-valued flags. serializeCascadePolicySet
 *  converts Sets → arrays; parseCascadePolicySet hydrates UNTRUSTED operator
 *  input back into a validated CascadePolicySet (arrays → Sets), returning null
 *  on ANY parse / structural / value / invariant error rather than throwing, so
 *  callers can fall back to a known-good default.
 * ────────────────────────────────────────────────────────────────────────── */

export interface SerializedCascadeStep {
  readonly fromOverdueHours: number;
  readonly stage: CascadeStage;
  readonly desired: CascadeDesiredState;
  readonly flags?: readonly CascadeFlag[];
}
export interface SerializedCascadePolicy {
  readonly version: string;
  readonly steps: readonly SerializedCascadeStep[];
}
export interface SerializedCascadePolicySet {
  readonly version: string;
  readonly byFrequency: Readonly<Record<RepaymentFrequency, SerializedCascadePolicy>>;
  readonly fallback: SerializedCascadePolicy;
}

/** Convert a policy set to its JSON-safe form (flags as arrays, empty sets omitted). */
export function serializeCascadePolicySet(set: CascadePolicySet): SerializedCascadePolicySet {
  const policy = (p: CascadePolicy): SerializedCascadePolicy => ({
    version: p.version,
    steps: p.steps.map((s) => ({
      fromOverdueHours: s.fromOverdueHours,
      stage: s.stage,
      desired: s.desired,
      ...(s.flags && s.flags.size > 0 ? { flags: Array.from(s.flags) } : {}),
    })),
  });
  return {
    version: set.version,
    byFrequency: {
      daily: policy(set.byFrequency.daily),
      weekly: policy(set.byFrequency.weekly),
      monthly: policy(set.byFrequency.monthly),
    },
    fallback: policy(set.fallback),
  };
}

const VALID_STAGES: ReadonlySet<number> = new Set([0, 1, 2, 3, 4, 5, 6, 7]);
const VALID_DESIRED: ReadonlySet<string> = new Set(["UNLOCKED", "SOFT_LOCKED", "HARD_LOCKED"]);
const VALID_FLAGS: ReadonlySet<string> = new Set([
  "alert_operator",
  "open_dispute",
  "flag_for_repossession",
  "notify_customer",
]);

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

/** Hydrate one untrusted step. Returns null on any malformed / out-of-range field. */
function hydrateStep(raw: unknown): CascadeStep | null {
  if (!isRecord(raw)) return null;
  const { fromOverdueHours, stage, desired, flags } = raw;
  if (typeof fromOverdueHours !== "number" || !Number.isFinite(fromOverdueHours)) return null;
  if (typeof stage !== "number" || !VALID_STAGES.has(stage)) return null;
  if (typeof desired !== "string" || !VALID_DESIRED.has(desired)) return null;
  let flagSet: ReadonlySet<CascadeFlag> | undefined;
  if (flags !== undefined) {
    if (!Array.isArray(flags)) return null;
    for (const f of flags) {
      if (typeof f !== "string" || !VALID_FLAGS.has(f)) return null;
    }
    flagSet = new Set(flags as CascadeFlag[]);
  }
  return {
    fromOverdueHours,
    stage: stage as CascadeStage,
    desired: desired as CascadeDesiredState,
    ...(flagSet ? { flags: flagSet } : {}),
  };
}

function hydratePolicy(raw: unknown): CascadePolicy | null {
  if (!isRecord(raw) || typeof raw.version !== "string" || !Array.isArray(raw.steps)) return null;
  const steps: CascadeStep[] = [];
  for (const s of raw.steps) {
    const step = hydrateStep(s);
    if (!step) return null;
    steps.push(step);
  }
  return { version: raw.version, steps };
}

/**
 * Parse + validate untrusted cascade-policy-set config (operator-authored).
 * Accepts a JSON string or an already-parsed object. Returns a validated
 * CascadePolicySet, or `null` on ANY parse / structural / value / invariant
 * error — callers fall back to a known-good default. NEVER throws.
 */
export function parseCascadePolicySet(raw: unknown): CascadePolicySet | null {
  let obj: unknown = raw;
  if (typeof raw === "string") {
    try {
      obj = JSON.parse(raw);
    } catch {
      return null;
    }
  }
  if (!isRecord(obj) || typeof obj.version !== "string" || !isRecord(obj.byFrequency)) return null;
  const daily = hydratePolicy(obj.byFrequency.daily);
  const weekly = hydratePolicy(obj.byFrequency.weekly);
  const monthly = hydratePolicy(obj.byFrequency.monthly);
  const fallback = hydratePolicy(obj.fallback);
  if (!daily || !weekly || !monthly || !fallback) return null;
  const set: CascadePolicySet = {
    version: obj.version,
    byFrequency: { daily, weekly, monthly },
    fallback,
  };
  try {
    validateCascadePolicySet(set);
  } catch {
    return null;
  }
  return set;
}
